import {
  Box,
  Button as ChakraButton,
  Flex,
  GridItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Spinner,
  Text
} from '@chakra-ui/react';
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  OutlineCard,
  Select,
  Switch,
  TagInput,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import {
  ReportConfig as DomainReportConfig,
  ReportsRequestSchema
} from '@publishing-engine/domain/actions/v1/reports';
import { isValidEmail } from '@publishing-engine/domain/validation';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generateReport } from 'src/api/mutations';
import {
  fetchSubmissionContext,
  fetchReportsConfigData,
  fetchWorkspaceJournals
} from 'src/api/queries';
import { ISubmissionContextListView } from '@publishing-engine/domain/views';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import Footer from 'src/components/Footer';
import { FieldLabel } from 'src/components/Field';
import MultiSelect from 'src/components/MultiSelect';
import Loading from 'src/pages/Status/Loading';
import { formatDateSafe } from 'src/utils/date';

type RequestSchemaField = DomainReportConfig['requestSchema'][number] & {
  options?: Array<{ label: string; value: string }>;
};

type EmailTag = {
  text: string;
};

type RequestSchemaPath = `requestSchema.${keyof ReportsRequestSchema}`;

type ReportsFormValues = {
  reportId: string;
  requestSchema: Partial<ReportsRequestSchema>;
  recipientsEmails: EmailTag[];
  sendEmail: boolean;
  selectedColumns: string[];
};

const getEmailsFromTags = (tags: EmailTag[]) =>
  tags.map(item => item.text.trim()).filter(Boolean);

const getDuplicateEmails = (emails: string[]) => {
  const seenEmails = new Set<string>();
  const duplicateEmails = new Set<string>();

  emails.forEach(email => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      return;
    }

    if (seenEmails.has(normalizedEmail)) {
      duplicateEmails.add(normalizedEmail);
      return;
    }

    seenEmails.add(normalizedEmail);
  });

  return Array.from(duplicateEmails);
};

const getRecipientsErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorRecord = error as {
    message?: unknown;
    root?: { message?: unknown };
  };

  if (typeof errorRecord.message === 'string') {
    return errorRecord.message;
  }

  if (typeof errorRecord.root?.message === 'string') {
    return errorRecord.root.message;
  }

  return undefined;
};

const getErrorStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorRecord = error as Record<string, unknown>;

  if (typeof errorRecord.status === 'number') {
    return errorRecord.status;
  }

  if (typeof errorRecord.statusCode === 'number') {
    return errorRecord.statusCode;
  }

  if ('response' in errorRecord && errorRecord.response) {
    return getErrorStatusCode(errorRecord.response);
  }

  if ('message' in errorRecord && errorRecord.message) {
    return getErrorStatusCode(errorRecord.message);
  }

  return undefined;
};

const UnauthorizedReportsState = () => (
  <OutlineCard p={{ base: 8, md: 12 }}>
    <Flex
      direction="column"
      align="center"
      justify="center"
      textAlign="center"
      minH="420px"
      gap={5}
    >
      <Flex
        align="center"
        justify="center"
        w="64px"
        h="64px"
        borderRadius="999px"
        bg="#EEF2FF"
        color="#3730A3"
      >
        <FontAwesomeIcon icon={faLockKeyhole} size="2x" />
      </Flex>
      <Box maxW="560px">
        <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="700" mb={2}>
          You do not have access to reports
        </Text>
        <Text color="#718096" fontSize="md">
          Unauthorized: User does not have access to perform this action.
          Contact your workspace administrator if you believe this is incorrect.
        </Text>
      </Box>
    </Flex>
  </OutlineCard>
);

const downloadXlsxFromBuffer = (
  buffer: ArrayBuffer,
  contentType: string,
  contentDisposition?: string
) => {
  const fileNameMatch = contentDisposition?.match(/filename="?([^"]+)"?/i);
  const fileName = fileNameMatch?.[1] ?? `report_${Date.now()}.xlsx`;
  const blob = new Blob([buffer], {
    type:
      contentType ||
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const formatCamelCaseLabel = (value: string = '') =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, char => char.toUpperCase());

const getDateRangeValidationMessage = (
  requestSchema: Partial<ReportsRequestSchema>
) => {
  const requestValues = requestSchema as Record<string, unknown>;
  const startDate =
    typeof requestValues.startDate === 'string' ? requestValues.startDate : '';
  const endDate =
    typeof requestValues.endDate === 'string' ? requestValues.endDate : '';

  if (startDate && endDate && startDate > endDate) {
    return 'From date cannot be later than End date';
  }

  return undefined;
};

const getDateRangeProps = (
  fieldKey: string,
  requestSchema: Partial<ReportsRequestSchema>
) => {
  const requestValues = requestSchema as Record<string, unknown>;

  if (fieldKey === 'startDate') {
    return {
      max:
        typeof requestValues.endDate === 'string'
          ? requestValues.endDate || undefined
          : undefined
    };
  }

  if (fieldKey === 'endDate') {
    return {
      min:
        typeof requestValues.startDate === 'string'
          ? requestValues.startDate || undefined
          : undefined
    };
  }

  return {};
};

const Reports = () => {
  const { workspaceId } = useWorkspace();
  const toast = useToast();
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const emptyJournalsData: ISubmissionContextListView = {
    collection: [],
    total: 0
  };

  const {
    data: journalsData = emptyJournalsData,
    isLoading: isLoadingJournals
  } = useQuery({
    queryKey: ['workspace-journals', { workspaceId }],
    queryFn: () =>
      workspaceId
        ? fetchWorkspaceJournals(workspaceId)
        : Promise.resolve(emptyJournalsData),
    enabled: !!workspaceId
  });

  const { data: reportConfigs = [], isLoading: isLoadingReportConfigs } =
    useQuery(
      ['reports-export-configs', { workspaceId }],
      (): Promise<DomainReportConfig[]> =>
        workspaceId ? fetchReportsConfigData(workspaceId) : Promise.resolve([]),
      {
        enabled: !!workspaceId,
        onError: (error: any) => {
          const status = getErrorStatusCode(error);
          if (status === 401) {
            setIsUnauthorized(true);
          } else if (status === 403) {
            toast({
              title: 'Forbidden',
              description:
                'Forbidden: You do not have access to this resource.',
              variant: 'attention'
            });
          }
        }
      }
    );

  const enabledReportOptions = reportConfigs.filter(report => !report.disabled);

  const {
    control,
    handleSubmit,
    register,
    setError,
    clearErrors,
    setValue,
    watch,
    formState: { errors }
  } = useForm<ReportsFormValues>({
    defaultValues: {
      reportId: '',
      requestSchema: {},
      recipientsEmails: [],
      sendEmail: false,
      selectedColumns: []
    }
  });
  const maxDate = formatDateSafe(new Date());
  const selectedReportId = watch('reportId');
  const selectedReport = enabledReportOptions.find(
    report => report.id === selectedReportId
  );
  const canSendByEmail = Boolean(selectedReport?.sendByEmail);
  const columnOptions =
    selectedReport?.columnConfig?.map(column => ({
      label: formatCamelCaseLabel(column),
      value: column
    })) ?? [];
  const selectedJournalCodes = watch('requestSchema.journalCode') ?? [];
  const sendEmail = watch('sendEmail');
  const selectedColumns = watch('selectedColumns');
  const recipientTags = watch('recipientsEmails');
  const requestSchemaValues = watch('requestSchema');
  const dateRangeValidationMessage =
    getDateRangeValidationMessage(requestSchemaValues);
  const manuscriptTypeFieldConfig = selectedReport?.requestSchema?.find(
    field => field.key === 'manuscriptType'
  );
  const hasManuscriptTypeField = Boolean(manuscriptTypeFieldConfig);
  const selectedJournals = journalsData?.collection?.filter(journal =>
    selectedJournalCodes.includes(journal.data.shortname)
  );
  const submissionContextQueries = useQueries({
    queries: (selectedJournals ?? []).map(journal => ({
      queryKey: ['submission-context', journal.id],
      queryFn: () => fetchSubmissionContext(journal.id),
      enabled: hasManuscriptTypeField
    }))
  });
  const articleTypeOptions = Array.from(
    new Map(
      submissionContextQueries
        .flatMap(query => query.data?.articleTypes ?? [])
        .map(articleType => [
          articleType.name,
          {
            label: articleType.name,
            value: articleType.name
          }
        ])
    ).values()
  );
  const { generateReportAsync, isLoading } = generateReport(
    selectedReportId || '',
    workspaceId ?? ''
  );

  useEffect(() => {
    setValue('requestSchema.manuscriptType', undefined);
  }, [selectedJournalCodes, selectedReportId, setValue]);

  useEffect(() => {
    setValue('selectedColumns', []);
  }, [selectedReportId, setValue]);

  useEffect(() => {
    if (!canSendByEmail && sendEmail) {
      setValue('sendEmail', false, {
        shouldDirty: true
      });
    }
  }, [canSendByEmail, sendEmail, setValue]);

  useEffect(() => {
    if (!sendEmail) {
      if ((recipientTags ?? []).length > 0) {
        setValue('recipientsEmails', [], {
          shouldDirty: true
        });
      }
      clearErrors('recipientsEmails');
      return;
    }

    const recipientsEmails = getEmailsFromTags(recipientTags ?? []);

    if (recipientsEmails.length === 0) {
      return;
    }

    const duplicateEmails = getDuplicateEmails(recipientsEmails);

    if (duplicateEmails.length > 0) {
      setError('recipientsEmails', {
        type: 'manual',
        message: 'Duplicate email addresses are not allowed'
      });
      return;
    }

    if (recipientsEmails.every(isValidEmail)) {
      clearErrors('recipientsEmails');
    }
  }, [clearErrors, recipientTags, sendEmail, setError, setValue]);

  const onSubmit = async (values: ReportsFormValues) => {
    if (!values.reportId) {
      toast({
        title: 'Select a report',
        description: 'Please select a report before generating.',
        variant: 'attention'
      });
      return;
    }

    clearErrors('recipientsEmails');

    const recipientsEmails = getEmailsFromTags(values.recipientsEmails);

    if (values.sendEmail && recipientsEmails.length === 0) {
      setError('recipientsEmails', {
        type: 'manual',
        message: 'Please enter at least one recipient email address'
      });
      return;
    }

    if (values.sendEmail && !recipientsEmails.every(isValidEmail)) {
      setError('recipientsEmails', {
        type: 'manual',
        message: 'Enter valid email address(es)'
      });
      return;
    }

    if (values.sendEmail && getDuplicateEmails(recipientsEmails).length > 0) {
      setError('recipientsEmails', {
        type: 'manual',
        message: 'Duplicate email addresses are not allowed'
      });
      return;
    }

    const requestSchemaDateValidationMessage = getDateRangeValidationMessage(
      values.requestSchema
    );

    if (requestSchemaDateValidationMessage) {
      toast({
        title: 'Invalid date range',
        description: requestSchemaDateValidationMessage,
        variant: 'attention'
      });
      return;
    }

    const normalizedRequestSchema: ReportsRequestSchema = {
      journalCode: values.requestSchema.journalCode ?? [],
      ...values.requestSchema
    };

    const response = await generateReportAsync({
      reportId: values.reportId,
      requestSchema: normalizedRequestSchema,
      recipientsEmails,
      sendEmail: values.sendEmail,
      selectedColumns: values.selectedColumns ?? []
    });

    if (!response?.isSuccess && getErrorStatusCode(response.message) === 401) {
      setIsUnauthorized(true);
      return;
    }

    if (!response?.isSuccess) {
      toast({
        title: 'Failed to generate report',
        description:
          typeof response?.message === 'string'
            ? response.message
            : 'Report generation failed. Please try again later',
        variant: 'error'
      });
      return;
    }

    const contentType = String(response.headers?.['content-type'] ?? '');
    const contentDisposition = String(
      response.headers?.['content-disposition'] ?? ''
    );
    const isXlsx = contentType.includes(
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );

    if (isXlsx && response.data instanceof ArrayBuffer) {
      downloadXlsxFromBuffer(response.data, contentType, contentDisposition);
      return;
    }

    const rawText =
      response.data instanceof ArrayBuffer
        ? new TextDecoder('utf-8').decode(new Uint8Array(response.data))
        : '';

    if (rawText && !contentType.includes('application/json')) {
      toast({
        title: values.sendEmail ? 'Report sent' : 'Report downloaded',
        description: rawText,
        variant: 'success'
      });
      return;
    }

    let parsedResponse: Record<string, unknown> | null = null;
    if (response.data instanceof ArrayBuffer) {
      if (rawText) {
        try {
          parsedResponse = JSON.parse(rawText) as Record<string, unknown>;
        } catch {
          parsedResponse = null;
        }
      }
    } else if (response.data && typeof response.data === 'object') {
      parsedResponse = response.data as Record<string, unknown>;
    }

    if (!parsedResponse) {
      toast({
        title: 'Unsupported response',
        description:
          'Report generated, but response is neither chart JSON nor XLSX.',
        variant: 'attention'
      });
      return;
    }
  };

  return (
    <Box
      id="scrollable-wrapper"
      overflowY="auto"
      overflowX="hidden"
      height="100%"
      width="100%"
      minWidth={0}
    >
      <Box height="100%" width="100%" minWidth={0}>
        <Box
          padding={{ base: 2, md: 5 }}
          minHeight="100%"
          width="100%"
          minWidth={0}
        >
          {isLoadingJournals || isLoadingReportConfigs ? (
            <Loading />
          ) : isUnauthorized ? (
            <UnauthorizedReportsState />
          ) : (
            <>
              <Flex justifyContent="space-between">
                <Text textStyle="headingSection">Reports</Text>
              </Flex>

              <OutlineCard
                padding={{ base: 3, md: 4 }}
                my={{ base: 3, md: 4 }}
                position="relative"
                width="100%"
                maxWidth="100%"
                minWidth={0}
              >
                {isLoading ? (
                  <Flex
                    position="absolute"
                    inset={0}
                    zIndex={10}
                    align="center"
                    justify="center"
                    bg="rgba(255, 255, 255, 0.72)"
                    backdropFilter="blur(2px)"
                  >
                    <Flex
                      align="center"
                      gap={3}
                      px={5}
                      py={3}
                      bg="white"
                      border="1px solid #E2E8F0"
                      borderRadius="12px"
                      boxShadow="0 12px 28px rgba(15, 23, 42, 0.08)"
                    >
                      <Spinner color="#1D4ED8" thickness="3px" />
                      <Text fontSize="sm" fontWeight="600" color="#1A202C">
                        Loading reports...
                      </Text>
                    </Flex>
                  </Flex>
                ) : null}
                <Box as="form" onSubmit={handleSubmit(onSubmit)} minWidth={0}>
                  <Flex
                    flexDirection="column"
                    gap={{ base: 4, md: 5 }}
                    width={{ base: '100%', md: '100%', lg: '70%', xl: '40%' }}
                    maxWidth={{ lg: '720px' }}
                    minWidth={0}
                  >
                    <Flex gap={2} flexDirection="column" width="100%">
                      <FieldLabel
                        subText
                        label="Report Type"
                        isRequired={true}
                      />
                      <Menu matchWidth>
                        <MenuButton
                          as={ChakraButton}
                          rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                          width="100%"
                          minH="40px"
                          height="auto"
                          textAlign="left"
                          justifyContent="space-between"
                          fontWeight="normal"
                          bg="surfacePrimaryFill"
                          border="1px solid"
                          borderColor="black"
                          _hover={{ bg: 'surfacePrimaryFill' }}
                          _active={{ bg: 'surfacePrimaryFill' }}
                          _expanded={{ bg: 'surfacePrimaryFill' }}
                          whiteSpace="normal"
                        >
                          {selectedReport?.name ?? 'Select Report'}
                        </MenuButton>
                        <MenuList maxH="320px" overflowY="auto">
                          {enabledReportOptions.map(report => (
                            <MenuItem
                              key={report.id}
                              onClick={() => {
                                setValue('reportId', report.id);
                                setValue('requestSchema', {});
                                setValue('selectedColumns', []);
                              }}
                              py={3}
                              whiteSpace="normal"
                            >
                              <Flex
                                direction="column"
                                align="flex-start"
                                gap={1}
                              >
                                <Text textStyle="textBody" fontWeight="medium">
                                  {report.name}
                                </Text>
                                {report.description && (
                                  <Text
                                    textStyle="textContext"
                                    color="text.secondary"
                                  >
                                    {report.description}
                                  </Text>
                                )}
                              </Flex>
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    </Flex>
                    {selectedReportId && (
                      <SimpleGrid
                        columns={{ base: 1, md: 2 }}
                        spacing={{ base: 4, md: 6 }}
                        width="100%"
                        minWidth={0}
                      >
                        {selectedReport?.requestSchema?.map(
                          (field: RequestSchemaField) => {
                            if (field.key === 'manuscriptType') {
                              return null;
                            }

                            const path = `requestSchema.${
                              field.key as keyof ReportsRequestSchema
                            }` as RequestSchemaPath;
                            const label = field.name;
                            const fieldValue = watch(path);

                            if (field.type === 'date') {
                              const dateRangeProps = getDateRangeProps(
                                String(field.key),
                                requestSchemaValues
                              );

                              return (
                                <GridItem
                                  key={field.key}
                                  colSpan={{ base: 1, md: 1 }}
                                >
                                  <Flex gap={2} flexDirection="column">
                                    <FieldLabel
                                      subText
                                      label={label}
                                      isRequired={field.required}
                                    />
                                    <DatePicker
                                      value={String(fieldValue ?? '')}
                                      {...dateRangeProps}
                                      {...register(path)}
                                      max={maxDate}
                                    />
                                    {field.key === 'endDate' &&
                                      dateRangeValidationMessage && (
                                        <Text
                                          textStyle="textContext"
                                          color="red.500"
                                        >
                                          {dateRangeValidationMessage}
                                        </Text>
                                      )}
                                  </Flex>
                                </GridItem>
                              );
                            }

                            if (field.type === 'boolean') {
                              return (
                                <GridItem
                                  key={field.key}
                                  colSpan={{ base: 1, md: 2 }}
                                >
                                  <Flex gap={2} flexDirection="column">
                                    <Checkbox
                                      id={field.key}
                                      name={field.key}
                                      ariaLabel={label}
                                      isRequired={field.required}
                                      isChecked={Boolean(fieldValue)}
                                      onChange={event =>
                                        setValue(path, event.target.checked, {
                                          shouldDirty: true
                                        })
                                      }
                                    >
                                      {label}
                                    </Checkbox>
                                  </Flex>
                                </GridItem>
                              );
                            }

                            const journalOptions =
                              field.optionsSource === 'journals'
                                ? journalsData?.collection?.map(journal => ({
                                    label: journal.data.name,
                                    value: journal.data.shortname
                                  })) ?? []
                                : [];

                            const options = field.options?.length
                              ? field.options
                              : journalOptions;

                            if (
                              field.key === 'journalCode' &&
                              options.length > 0
                            ) {
                              const selectedValues = Array.isArray(fieldValue)
                                ? fieldValue
                                : typeof fieldValue === 'string' && fieldValue
                                ? [fieldValue]
                                : [];

                              return (
                                <GridItem
                                  key={field.key}
                                  colSpan={{ base: 1, md: 2 }}
                                >
                                  <Flex gap={2} flexDirection="column">
                                    <FieldLabel
                                      subText
                                      label={label}
                                      isRequired={field.required}
                                    />
                                    <MultiSelect
                                      options={options}
                                      value={selectedValues}
                                      onChange={value =>
                                        setValue(path, value, {
                                          shouldDirty: true
                                        })
                                      }
                                      placeholder={`Select ${field.name}`}
                                      variant="interactive"
                                      showSelectAllOption
                                    />
                                  </Flex>
                                </GridItem>
                              );
                            }

                            if (options.length > 0) {
                              return (
                                <GridItem
                                  key={field.key}
                                  colSpan={{ base: 1, md: 2 }}
                                >
                                  <Flex gap={2} flexDirection="column">
                                    <FieldLabel
                                      subText
                                      label={label}
                                      isRequired={field.required}
                                    />
                                    <Select
                                      value={String(fieldValue ?? '')}
                                      onChange={event =>
                                        setValue(path, event.target.value, {
                                          shouldDirty: true
                                        })
                                      }
                                    >
                                      <option value="">
                                        Select {field.name}
                                      </option>
                                      {options.map(
                                        (option: {
                                          label: string;
                                          value: string;
                                        }) => (
                                          <option
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.label}
                                          </option>
                                        )
                                      )}
                                    </Select>
                                  </Flex>
                                </GridItem>
                              );
                            }

                            return (
                              <GridItem
                                key={field.key}
                                colSpan={{ base: 1, md: 2 }}
                              >
                                <Flex gap={2} flexDirection="column">
                                  <FieldLabel
                                    subText
                                    label={label}
                                    isRequired={field.required}
                                  />
                                  <Input
                                    name={field.key}
                                    value={String(fieldValue ?? '')}
                                    onChange={event =>
                                      setValue(path, event.target.value, {
                                        shouldDirty: true
                                      })
                                    }
                                    placeholder={`Enter ${field.name}`}
                                  />
                                </Flex>
                              </GridItem>
                            );
                          }
                        )}
                        {hasManuscriptTypeField && (
                          <GridItem colSpan={{ base: 1, md: 2 }}>
                            <Flex gap={2} flexDirection="column">
                              <FieldLabel
                                subText
                                label={manuscriptTypeFieldConfig?.name ?? ''}
                                isRequired={manuscriptTypeFieldConfig?.required}
                              />
                              <Select
                                value={String(
                                  watch('requestSchema.manuscriptType') ?? ''
                                )}
                                onChange={event =>
                                  setValue(
                                    'requestSchema.manuscriptType',
                                    event.target.value || undefined,
                                    {
                                      shouldDirty: true
                                    }
                                  )
                                }
                                isDisabled={
                                  selectedJournalCodes.length === 0 ||
                                  articleTypeOptions.length === 0
                                }
                              >
                                <option value="">
                                  {selectedJournalCodes.length > 0
                                    ? 'Select Manuscript Type'
                                    : 'Select Journal first'}
                                </option>
                                {articleTypeOptions.map(option => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </Select>
                            </Flex>
                          </GridItem>
                        )}
                      </SimpleGrid>
                    )}
                    {selectedReportId && canSendByEmail && (
                      <Flex
                        gap={{ base: 3, md: 2 }}
                        flexDirection={{ base: 'column', sm: 'row' }}
                        alignItems={{ base: 'flex-start', sm: 'center' }}
                      >
                        <FieldLabel
                          subText
                          label="Send Report by Email"
                          isRequired={false}
                        />
                        <Switch
                          isChecked={Boolean(sendEmail)}
                          onChange={event =>
                            setValue('sendEmail', event.target.checked, {
                              shouldDirty: true
                            })
                          }
                        />
                      </Flex>
                    )}
                    {selectedReportId && canSendByEmail && sendEmail && (
                      <Flex gap={2} flexDirection="column">
                        <FieldLabel
                          subText
                          label="Recipient's Email"
                          isRequired={true}
                        />
                        <TagInput
                          control={control}
                          name="recipientsEmails"
                          placeholder="Enter email and press Enter"
                          isInvalid={!!errors.recipientsEmails}
                          errorMessage={getRecipientsErrorMessage(
                            errors.recipientsEmails
                          )}
                        />
                      </Flex>
                    )}
                    {selectedReportId && columnOptions.length > 0 && (
                      <Flex gap={2} flexDirection="column">
                        <FieldLabel
                          subText
                          label="Columns"
                          isRequired={false}
                        />
                        <MultiSelect
                          options={columnOptions}
                          value={selectedColumns ?? []}
                          variant="interactive"
                          onChange={value =>
                            setValue('selectedColumns', value, {
                              shouldDirty: true
                            })
                          }
                          placeholder="Select columns"
                          showSelectAllOption
                        />
                      </Flex>
                    )}
                  </Flex>

                  <Button
                    type="submit"
                    width={{ base: '100%', sm: 'auto' }}
                    mx={{ base: 0, sm: 'auto' }}
                    my={{ base: 5, md: 6 }}
                    variant="primary"
                    isLoading={isLoading}
                    whiteSpace="normal"
                    minWidth={0}
                  >
                    <Text as="span" textAlign="center" whiteSpace="normal">
                      {canSendByEmail && sendEmail
                        ? 'Generate and Send Report by Email'
                        : 'Generate and Download Report'}
                    </Text>
                  </Button>
                </Box>
              </OutlineCard>
            </>
          )}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default Reports;
